import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Select from 'react-select'; // Import Select from react-select
import { X, Plus, Minus, Package, ShoppingBag, DollarSign, Tag, Box, Info, Search, Edit } from 'lucide-react';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { API_BASE_PATH, fetchCatentries, fetchStores, fetchProductsForStore } from '../apiHelper';

// Initialize the API client
const client = new FastAPIClient();

// Define ItemTypes for React DND
const ItemTypes = {
    PRODUCT: 'product'
};

/**
 * DraggableProduct Component.
 * Represents an individual product that can be dragged into the package composer.
 * Displays product details, price, and stock information.
 * Now includes an 'Add' button for click-to-add functionality.
 *
 * @param {object} props - Component props.
 * @param {object} props.product - The product object to display.
 * @param {function} props.onAddProductClick - Callback to add the product to the package.
 */
const DraggableProduct = ({ product, onAddProductClick }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.PRODUCT,
        item: {
            id: product.id,
            isService: product.isService,
            name: product.name,
            price: product.price,
            priceSymbol: product.priceSymbol,
            stock: product.stock
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        })
    }));

    const isOutOfStock = product.stock === 0 && !product.isService;

    return (
        <Card
            ref={drag} // Apply drag ref to the Card component
            className={`cursor-grab transition-all duration-200 p-4 relative
            ${isDragging ? "opacity-50 border-gmail-blue bg-blue-50 shadow-lg" : "hover:bg-gray-100"}
            ${isOutOfStock ? "bg-red-50 text-gray-400 cursor-not-allowed opacity-70" : ""}`}
            style={{ touchAction: 'none' }} // Helps with drag behavior on touch devices
        >
            <h3 className="text-lg font-semibold text-text-primary mb-1">{product.name}</h3>
            <p className="text-sm text-text-secondary mb-2">{product.description}</p>
            <p className='text-md text-text-primary font-bold mt-2'>
                <DollarSign size={16} className="inline-block mr-1 text-gmail-green" />
                Price: {product.priceSymbol}{product.price.toFixed(2)}
            </p>
            <p className='text-xs text-text-secondary mt-1'>
                <Box size={14} className="inline-block mr-1 text-gmail-yellow" />
                In Stock: {product.isService ? 'Service (Unlimited)' : `${product.stock}`}
            </p>
            {isOutOfStock && <p className='text-xs text-gmail-red font-semibold mt-1'>Out of Stock</p>}

            {/* Add Button */}
            <Button
                type="button"
                onClick={() => onAddProductClick(product)}
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 p-1.5 rounded-full text-gmail-blue hover:bg-blue-100
                           focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue"
                disabled={isOutOfStock} // Disable if out of stock and not a service item
                aria-label={`Add ${product.name} to package`}
            >
                <Plus size={18} />
            </Button>
        </Card>
    );
};

/**
 * ProductList Component.
 * Displays a list of available products that can be dragged or added via button.
 * Now includes a search bar.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.products - List of products to display.
 * @param {function} props.onAddProduct - Callback to add a product to the package.
 */
const ProductList = ({ products, onAddProduct }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredProducts = useMemo(() => {
        if (!searchTerm.trim()) {
            return products;
        }
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return products.filter(product =>
            product.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            product.description.toLowerCase().includes(lowerCaseSearchTerm) ||
            product.catalog.toLowerCase().includes(lowerCaseSearchTerm) ||
            product.category.toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [products, searchTerm]);

    return (
        <div className="flex-1 p-6 bg-gray-50 border-l border-gray-200 overflow-y-auto custom-scrollbar rounded-lg ml-4">
            <h2 className="text-xl font-bold mb-4 text-text-primary">Available Products</h2>

            {/* Search Bar */}
            <div className="mb-6 relative">
                <Input
                    type="text"
                    placeholder="Search products by name, part number, catalog, or category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                />
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-dark pointer-events-none" />
            </div>


            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                        <DraggableProduct key={product.id} product={product} onAddProductClick={onAddProduct} />
                    ))
                ) : (
                    <div className="col-span-full text-center text-text-secondary py-8">
                        <Info size={32} className="mx-auto mb-2 text-gray-400" />
                        <p>{searchTerm.trim() ? "No matching products found." : "No products available. Please create some products first."}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

/**
 * PackageComposer Component.
 * Allows users to drag products into a package, adjust quantities, and save the package.
 *
 * @param {object} props - Component props.
 * @param {function} props.onSavePackage - Callback function to save the package.
 * @param {Array<object>} props.availableProducts - The full list of available products to check stock against.
 * @param {object} props.onAddProductFromList - Ref object for exposing internal functions to parent.
 */
const PackageComposer = ({ onSavePackage, availableProducts, onAddProductFromList }) => {
    const { showToast } = useToast();
    const [packageItems, setPackageItems] = useState([]);
    const [packageName, setPackageName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isPriceOverridden, setIsPriceOverridden] = useState(false); // New state for price override toggle
    const [overriddenPrice, setOverriddenPrice] = useState(''); // New state for overridden price value

    // Helper to get current stock from the available products list
    const getProductStock = useCallback((productId) => {
        const product = availableProducts.find(p => p.id === productId);
        return product ? product.stock : 0;
    }, [availableProducts]);

    // This function encapsulates the logic for adding an item to the package,
    // whether via drag-and-drop or click.
    const addItemToPackage = useCallback((itemToAdd) => {
        const currentStock = getProductStock(itemToAdd.id);
        const isServiceItem = itemToAdd.isService;

        setPackageItems((prevItems) => {
            const existingItemIndex = prevItems.findIndex((pkgItem) => pkgItem.id === itemToAdd.id);

            if (existingItemIndex > -1) {
                const updatedItems = [...prevItems];
                if (isServiceItem || (updatedItems[existingItemIndex].quantity < currentStock)) {
                    updatedItems[existingItemIndex].quantity += 1;
                    setTimeout(() => showToast(`Added another "${itemToAdd.name}" to package.`, 'info'), 0);
                    return updatedItems;
                } else {
                    setTimeout(() => showToast(`Cannot add more "${itemToAdd.name}". Max stock reached.`, 'error'), 0);
                    return prevItems;
                }
            } else {
                const fullProductDetails = availableProducts.find(p => p.id === itemToAdd.id);
                if (fullProductDetails) {
                    setTimeout(() => showToast(`Added "${itemToAdd.name}" to package.`, 'success'), 0);
                    return [...prevItems, { ...fullProductDetails, quantity: 1 }];
                }
                setTimeout(() => showToast(`Error: Product "${itemToAdd.name}" not found.`, 'error'), 0);
                return prevItems;
            }
        });
    }, [getProductStock, showToast, availableProducts]);


    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ItemTypes.PRODUCT,
        drop: (item) => {
            addItemToPackage(item);
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }));

    const handleQuantityChange = useCallback((productId, delta) => {
        setPackageItems((prevItems) => {
            const updatedItems = prevItems.map((item) => {
                if (item.id === productId) {
                    const newQuantity = Math.max(1, item.quantity + delta);
                    const currentStock = getProductStock(productId);
                    const isServiceItem = item.isService;

                    if (isServiceItem || newQuantity <= currentStock) {
                        return { ...item, quantity: newQuantity };
                    } else {
                        setTimeout(() => showToast(`Cannot set quantity to ${newQuantity} for "${item.name}". Max stock: ${currentStock}.`, 'error'), 0);
                        return item;
                    }
                }
                return item;
            });
            return updatedItems;
        });
    }, [getProductStock, showToast]);

    const handleRemoveItem = useCallback((productId) => {
        setPackageItems((prevItems) => {
            const removedItem = prevItems.find(item => item.id === productId);
            if (removedItem) {
                setTimeout(() => showToast(`Removed "${removedItem.name}" from package.`, 'info'), 0);
            }
            return prevItems.filter((item) => item.id !== productId);
        });
    }, [showToast]);

    // Calculate total price: either sum of items or overridden price
    const calculateTotalPrice = useCallback(() => {
        if (isPriceOverridden && overriddenPrice !== '') {
            return parseFloat(overriddenPrice);
        }
        return packageItems.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0
        );
    }, [packageItems, isPriceOverridden, overriddenPrice]);

    const handleSubmitPackage = async () => {
        if (packageItems.length === 0) {
            showToast("Please add items to the package.", 'error');
            return;
        }
        if (!packageName.trim()) {
            showToast("Please enter a name for the package.", 'error');
            return;
        }
        if (isPriceOverridden && (overriddenPrice === '' || isNaN(parseFloat(overriddenPrice)))) {
            showToast("Please enter a valid overridden price.", 'error');
            return;
        }


        setIsSaving(true);
        try {
            // Pass the calculated total price to the parent's onSavePackage
            await onSavePackage(packageName, packageItems, calculateTotalPrice());
            showToast("Package saved and inventory updated successfully!", 'success');
            setPackageItems([]); // Clear package items
            setPackageName(''); // Clear package name
            setIsPriceOverridden(false); // Reset override toggle
            setOverriddenPrice(''); // Clear overridden price
        } catch (err) {
            showToast(`Failed to save package: ${err.message || err}`, 'error');
            console.error("Error saving package:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const isActive = canDrop && isOver;
    let borderColor = 'border-gray-300';
    if (isActive) {
        borderColor = 'border-gmail-blue ring-2 ring-gmail-blue bg-blue-50';
    } else if (canDrop) {
        borderColor = 'border-blue-300';
    }

    // Expose functions to the parent via the ref
    useEffect(() => {
        if (onAddProductFromList) {
            onAddProductFromList.current = {
                addItem: addItemToPackage,
                // No longer need to expose these directly, as total price is passed
                // getIsPriceOverridden: () => isPriceOverridden,
                // getOverriddenPrice: () => overriddenPrice,
            };
        }
    }, [addItemToPackage, onAddProductFromList]);


    return (
        <div className='flex-1 p-6 bg-white overflow-y-auto custom-scrollbar rounded-lg'>
            <h2 className='text-xl font-bold mb-4 text-text-primary'>Package Composer</h2>

            <div className='mb-4'>
                <Input
                    label="Package Name"
                    id="packageName"
                    name="packageName"
                    type="text"
                    value={packageName}
                    onChange={(e) => setPackageName(e.target.value)}
                    placeholder="e.g. 'Base Tuition + ICT' or 'Half-Pack Canned Maltina'"
                    required
                />
            </div>

            <div
                ref={drop}
                className={`min-h-64 border-2 border-dashed rounded-lg p-4 transition-colors duration-200
                ${borderColor} ${packageItems.length === 0 ? 'flex items-center justify-center text-gray-400' : ''}`}
            >
                {packageItems.length === 0 ? (
                    <div className="text-center">
                        <ShoppingBag size={48} className="mx-auto mb-3 text-gray-400" />
                        <p className="text-lg font-medium">Drag products here to build your package</p>
                    </div>
                ) : (
                    <div className='space-y-3'>
                        {packageItems.map((item) => (
                            <div
                                key={item.id}
                                className='flex items-center p-3 border border-gray-200 rounded-lg bg-white shadow-sm
                                transition-transform duration-200 hover:scale-[1.01] hover:shadow-md'
                            >
                                <div className='flex-grow'>
                                    <p className='font-medium text-text-primary'>{item.name}</p>
                                    <p className='text-sm text-text-secondary'>
                                        Price: {item.priceSymbol}{item.price.toFixed(2)} ea.
                                    </p>
                                    {item.isService ? (
                                        <p className='text-xs text-text-secondary'>Service Item</p>
                                    ) : (
                                        <p className='text-xs text-text-secondary'>In Stock: {getProductStock(item.id)}</p>
                                    )}
                                </div>
                                <div className='flex items-center space-x-2'>
                                    <Button
                                        onClick={() => handleQuantityChange(item.id, -1)}
                                        variant="ghost"
                                        size="sm"
                                        className="p-1.5 rounded-full text-gray-700 hover:bg-gray-200"
                                        disabled={item.quantity <= 1}
                                    >
                                        <Minus size={16} />
                                    </Button>
                                    <span className='font-semibold text-text-primary'>{item.quantity}</span>
                                    <Button
                                        onClick={() => handleQuantityChange(item.id, 1)}
                                        variant="ghost"
                                        size="sm"
                                        className="p-1.5 rounded-full text-gray-700 hover:bg-gray-200"
                                        disabled={!item.isService && item.quantity >= getProductStock(item.id)}
                                    >
                                        <Plus size={16} />
                                    </Button>
                                    <Button
                                        onClick={() => handleRemoveItem(item.id)}
                                        variant="ghost"
                                        size="sm"
                                        className="p-1.5 rounded-full text-gmail-red hover:bg-red-100 ml-2"
                                    >
                                        <X size={16} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className='mt-6 flex flex-col items-end'>
                {/* Price Override Toggle */}
                <div className="flex items-center mb-2">
                    <input
                        type="checkbox"
                        id="overridePrice"
                        checked={isPriceOverridden}
                        onChange={(e) => setIsPriceOverridden(e.target.checked)}
                        className="form-checkbox h-4 w-4 text-gmail-blue transition duration-150 ease-in-out rounded focus:ring-gmail-blue"
                    />
                    <label htmlFor="overridePrice" className="ml-2 text-sm font-medium text-text-secondary">
                        Override Total Price
                    </label>
                </div>

                {isPriceOverridden && (
                    <div className="w-full max-w-xs mb-4">
                        <Input
                            label="Override Price"
                            id="overriddenPrice"
                            name="overriddenPrice"
                            type="number"
                            placeholder="Enter custom total price"
                            value={overriddenPrice}
                            onChange={(e) => setOverriddenPrice(e.target.value)}
                            required
                            icon={<Edit size={18} className="text-gray-dark" />}
                        />
                    </div>
                )}

                <p className='text-lg font-bold text-text-primary mb-4'>
                    Total Package Price: {packageItems[0]?.priceSymbol || '$'}{calculateTotalPrice().toFixed(2)}
                </p>

                <Button
                    onClick={handleSubmitPackage}
                    variant="primary"
                    size="lg"
                    className="flex items-center gap-2"
                    disabled={packageItems.length === 0 || !packageName.trim() || isSaving || (isPriceOverridden && (overriddenPrice === '' || isNaN(parseFloat(overriddenPrice))))}
                >
                    <Package size={20} />
                    {isSaving ? 'Saving Package...' : 'Save Package & Update Inventory'}
                </Button>
            </div>
        </div>
    );
};

/**
 * CreatePackageItem Component.
 * The main page component for creating packages.
 * Manages fetching available products and orchestrates the DND context.
 */
const CreatePackageItem = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // New state for stores and selected store
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState(null);
    const onAddProductFromListRef = React.useRef(null);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to build packages.", "info");
            navigate('/login')
        } else {
            setLoggedInUser(JSON.parse(lcUser));
        }
    }, [showToast]);

    useEffect(() => {
        const loadAllData = async () => {
            if (loggedInUser) {
                setLoading(true)
                setError(null)
                try {
                    const fetchedStores = await fetchStores(loggedInUser);
                    
                    const storeOptions = fetchedStores.map(store => ({
                        label: store.identifier,
                        value: store.store_id,
                        address: store.address
                    }));
                    setStores(storeOptions);

                    if (storeOptions.length > 0) {
                        const defaultStore = storeOptions[0];
                        setSelectedStore(defaultStore);

                        // Fetch products for the first store
                        const productPayload = {
                            ...loggedInUser,
                            store_id: defaultStore.value,
                            offset: 0,
                            limit: 10000
                        };
                        const fetchedProducts = await fetchProductsForStore(productPayload);
                        const formattedProducts = fetchedProducts.map((item) => {
                            const isService = item.catgpenrels?.[0]?.catalog?.identifier?.toLowerCase().includes("service");
                            let totalStock = 0;
                            if (item.inventory_lots && Array.isArray(item.inventory_lots)) {
                                totalStock = item.inventory_lots.reduce((sum, lot) => sum + (lot.current_quantity || 0), 0);
                            }
                            return {
                                id: item.catentry_id,
                                name: item.productname,
                                description: item.partnumber || item.productname,
                                price: item.listprice?.listprice || 0,
                                priceSymbol: item.listprice?.currency_rel?.setcnote || "$",
                                stock: isService ? 0 : totalStock,
                                category: item.catgpenrels?.[0]?.category?.identifier || "Uncategorized",
                                catalog: item.catgpenrels?.[0]?.catalog?.identifier || "Unattached",
                                owner: item.owner?.organization?.organization_name || "N/A",
                                attachmenturl: item.attachments?.[0]?.attachment?.attachmenturl || null,
                                isService: isService
                            };
                        });
                        setProducts(formattedProducts);
                    } else {
                        setProducts([]);
                    }
    
                } catch (err) {
                    console.error("Error loading package creation data:",err);
                    showToast(err.response?.data?.detail || "Failed to load necessary data. Please try again.", "error");
                    setError(err.response?.data?.detail || "Failed to load necessary data. Please try again.");
                } finally {
                    setLoading(false)
                }
            }
        };
        loadAllData()
    },[loggedInUser, showToast])

    // New useEffect to handle store selection changes
    useEffect(() => {
        const fetchProductsByStore = async () => {
            if (loggedInUser && selectedStore) {
                setLoading(true);
                setError(null);
                try {
                    const productPayload = {
                        ...loggedInUser,
                        store_id: selectedStore.value,
                        offset: 0,
                        limit: 10000
                    };
                    const fetchedProducts = await fetchProductsForStore(productPayload);
                    const formattedProducts = fetchedProducts.map((item) => {
                        const isService = item.catgpenrels?.[0]?.catalog?.identifier?.toLowerCase().includes("service");
                        let totalStock = 0;
                        if (item.inventory_lots && Array.isArray(item.inventory_lots)) {
                            totalStock = item.inventory_lots.reduce((sum, lot) => sum + (lot.current_quantity || 0), 0);
                        }
                        return {
                            id: item.catentry_id,
                            name: item.productname,
                            description: item.partnumber || item.productname,
                            price: item.listprice?.listprice || 0,
                            priceSymbol: item.listprice?.currency_rel?.setcnote || "$",
                            stock: isService ? 0 : totalStock,
                            category: item.catgpenrels?.[0]?.category?.identifier || "Uncategorized",
                            catalog: item.catgpenrels?.[0]?.catalog?.identifier || "Unattached",
                            owner: item.owner?.organization?.organization_name || "N/A",
                            attachmenturl: item.attachments?.[0]?.attachment?.attachmenturl || null,
                            isService: isService
                        };
                    });
                    setProducts(formattedProducts);
                } catch (err) {
                    console.error("Error fetching products for store:", err);
                    showToast(err.response?.data?.detail || "Failed to load products for the selected store.", "error");
                    setError(err.response?.data?.detail || "Failed to load products for the selected store.");
                    setProducts([]); // Clear products on error
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchProductsByStore();
    }, [selectedStore, loggedInUser, showToast]);

    // Updated handleSavePackage to accept totalPackagePrice and refresh products for the selected store
    const handleSavePackage = async (packageName, packageItems, totalPackagePrice) => {
        if (!loggedInUser || !loggedInUser.users_id) {
            showToast("User not logged in. Cannot save package.", "error");
            throw new Error("User not logged in.");
        }

        const itemsList = packageItems.map(item => {
            return {
                id: item.id,
                isService: item.isService,
                name: item.name,
                price: item.price, // Keep original unit price
                priceSymbol: item.priceSymbol,
                stock: item.stock,
                quantity: item.quantity
            };
        });

        const payload = {
            users_id: loggedInUser.users_id,
            package_items: itemsList,
            package_name: packageName, // New field
            total_price: totalPackagePrice, // New field
            store_id: selectedStore.value, // New field
        };

        console.log('Sending package creation payload:', payload);

        try {
            const response = await client.postData(`${API_BASE_PATH}/product/create-package`, payload);
            console.log('Package created successfully:', response);

            if (loggedInUser && selectedStore) {
                const productPayload = {
                    ...loggedInUser,
                    store_id: selectedStore.value,
                    offset: 0,
                    limit: 10000
                };
                const updatedProducts = await fetchProductsForStore(productPayload);
                const formattedUpdatedProducts = updatedProducts.map((item) => {
                    const isService = item.catgpenrels?.[0]?.catalog?.identifier?.toLowerCase().includes("service");
                    let totalStock = 0;
                    if (item.inventory_lots && Array.isArray(item.inventory_lots)) {
                        totalStock = item.inventory_lots.reduce((sum, lot) => sum + (lot.current_quantity || 0), 0);
                    }
                    return {
                        id: item.catentry_id,
                        name: item.productname,
                        description: item.partnumber || item.productname,
                        price: item.listprice?.listprice || 0,
                        priceSymbol: item.listprice?.currency_rel?.setcnote || "$",
                        stock: isService ? 0 : totalStock,
                        category: item.catgpenrels?.[0]?.category?.identifier || "Uncategorized",
                        catalog: item.catgpenrels?.[0]?.catalog?.identifier || "Unattached",
                        owner: item.owner?.organization?.organization_name || "N/A",
                        attachmenturl: item.attachments?.[0]?.attachment?.attachmenturl || null,
                        isService: isService
                    };
                });
                setProducts(formattedUpdatedProducts);
            }
            return response;
        } catch (err) {
            console.error("Error saving package from app:", err);
            const errorMessage = err.response?.data?.detail || `Failed to save package: ${err.message || err}`;
            showToast(errorMessage, 'error');
            throw err;
        }
    };

    const handleAddProductClick = useCallback((product) => {
        if (onAddProductFromListRef.current) {
            onAddProductFromListRef.current.addItem(product);
        }
    }, []);

    if (loading) {
        return (
            <div className='flex items-center justify-center min-h-[60vh] text-center p-8 text-text-secondary'>
                <Info size={32} className="animate-bounce text-gmail-blue" />
                <p className="ml-3 text-lg">Loading products...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-gmail-red'>
                <X size={32} className="mb-2" />
                <p className="text-lg mb-4">Error: {error}</p>
                <Button onClick={() => window.location.reload()} variant="primary">
                    Reload Page
                </Button>
            </div>
        );
    }

    if (!loggedInUser) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to build packages.</p>
                <Button onClick={() => navigate('/login')} variant="primary">
                    Go to Login
                </Button>
            </div>
        );
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <div className='flex flex-col lg:flex-row h-[calc(100vh-100px)] bg-gray-100 p-6 rounded-lg shadow-inner'>
                {/* Left Pane: Package Composer */}
                <PackageComposer
                    onSavePackage={handleSavePackage}
                    availableProducts={products}
                    // Pass a ref object that PackageComposer can populate with its internal functions
                    onAddProductFromList={onAddProductFromListRef}
                />

                {/* Right Pane: Product List */}
                <div className='flex-1 p-6 bg-gray-50 border-l border-gray-200 overflow-y-auto custom-scrollbar rounded-lg ml-4'>
                    <h2 className="text-xl font-bold mb-4 text-text-primary">Available Products</h2>
                    <div className="mb-6">
                        <label htmlFor="store-select" className="block text-sm font-medium text-text-primary mb-1">Select Store</label>
                        <Select
                            id="store-select"
                            options={stores}
                            value={selectedStore}
                            onChange={setSelectedStore}
                            placeholder="Select a store..."
                            isSearchable
                            classNamePrefix="react-select"
                        />
                    </div>
                    <ProductList products={products} onAddProduct={handleAddProductClick} />
                </div>
            </div>
        </DndProvider>
    );
};

export default CreatePackageItem;
